import React, { useMemo } from "react";
import { Clock } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { cn } from "@/lib/utils";

/**
 * TimePicker — compact dropdown of HH:mm values in stepMin increments.
 * Used inside TimeSlotsEditor and anywhere a sleek time selector is needed.
 */
export default function TimePicker({
  value = "",
  onChange,
  stepMin = 15,
  testId = "time-picker",
  className,
}) {
  const times = useMemo(() => {
    const list = [];
    for (let m = 0; m < 24 * 60; m += stepMin) {
      const h = String(Math.floor(m / 60)).padStart(2, "0");
      const mn = String(m % 60).padStart(2, "0");
      list.push(`${h}:${mn}`);
    }
    return list;
  }, [stepMin]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn("h-7 px-2 text-xs font-mono", className)}
          data-testid={`${testId}-trigger`}
        >
          <Clock className="w-3 h-3 mr-1" />
          {value || "--:--"}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-28 p-0 max-h-64 overflow-y-auto"
        align="start"
        data-testid={`${testId}-list`}
      >
        {times.map(t => (
          <button
            key={t}
            type="button"
            onClick={() => onChange?.(t)}
            className={cn(
              "w-full text-left px-3 py-1.5 text-xs font-mono border-b border-border last:border-0 transition-colors",
              t === value ? "bg-primary text-primary-foreground" : "hover:bg-accent"
            )}
            data-testid={`${testId}-opt-${t}`}
          >
            {t}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
